import { useEffect, useState } from "react";
import {
  createLinearIssue,
  getLinearInstallation,
  LinearApiError,
  type CreatedLinearIssue,
  type LinearInstallationView,
  type RunHealDetail,
} from "../lib/api";

/**
 * Small modal for filing a heal as a Linear issue. Mirrors HealPrDialog's
 * shape (modal, prefilled title/body, single primary action, error banner)
 * but the wire format is much simpler — just title/description/teamId.
 *
 * Pre-flight: fetches GET /api/integrations/linear once on open. Two
 * states:
 *   - not connected → "Connect Linear first" link to /account#linear
 *   - connected     → form with team picker + title + description
 *
 * Success: shows the new issue identifier ("ENG-42") and a deep link
 * before the user dismisses. We don't refresh the heal row — Linear
 * issues are tracked server-side as a delivery log entry rather than a
 * column on the heal (forward-compat: the same heal can be sent to
 * GitHub *and* Linear).
 */
export function HealLinearDialog(props: {
  heal: RunHealDetail;
  onClose: () => void;
}): JSX.Element {
  const { heal, onClose } = props;

  const [installation, setInstallation] = useState<LinearInstallationView | null>(
    null,
  );
  const [loaded, setLoaded] = useState(false);
  const [teamId, setTeamId] = useState<string>("");
  const [title, setTitle] = useState<string>(defaultIssueTitle(heal));
  const [description, setDescription] = useState<string>(defaultIssueBody(heal));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<CreatedLinearIssue | null>(null);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);

  // Load installation once on open.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inst = await getLinearInstallation();
        if (cancelled) return;
        setInstallation(inst);
        // Default the picker to whatever the user marked as default.
        if (inst?.defaultTeamId) setTeamId(inst.defaultTeamId);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof LinearApiError && err.code === "INTEGRATION_NOT_ALLOWED") {
          setNeedsUpgrade(true);
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setBusy(true);
    try {
      const issue = await createLinearIssue({
        title: title.trim(),
        description,
        ...(teamId ? { teamId } : {}),
      });
      setSuccess(issue);
    } catch (err) {
      if (err instanceof LinearApiError) {
        if (err.code === "INTEGRATION_NOT_ALLOWED") setNeedsUpgrade(true);
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Send as Linear issue"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        // Click on backdrop = close, click inside = ignore.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-100">
            Send as Linear issue
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error && (
          <div className="mb-3 rounded-md border border-rose-700/60 bg-rose-900/30 px-3 py-2 text-sm text-rose-200">
            {error}
            {needsUpgrade && (
              <>
                {" "}
                <a className="underline" href="/account#plan">
                  Upgrade to Pro
                </a>
                .
              </>
            )}
          </div>
        )}

        {success ? (
          <div className="rounded-md border border-emerald-700/60 bg-emerald-900/30 p-4 text-sm text-emerald-100">
            <p className="font-medium">
              Created{" "}
              <a
                href={success.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                {success.identifier}
              </a>
            </p>
            <p className="mt-1 text-emerald-300/80">{success.title}</p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-emerald-700 px-3 py-1.5 text-xs text-emerald-100 hover:bg-emerald-900/50"
              >
                Done
              </button>
              <a
                href={success.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500"
              >
                Open in Linear →
              </a>
            </div>
          </div>
        ) : !loaded ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !installation ? (
          <div className="rounded-md border border-slate-700 bg-slate-800/40 p-4 text-sm text-slate-300">
            Linear isn't connected on this account yet.{" "}
            <a className="underline" href="/account#linear">
              Connect Linear
            </a>{" "}
            to file heal proposals as issues.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-300">
                Workspace
              </label>
              <p className="mt-1 text-sm text-slate-200">
                {installation.workspaceName}
                {installation.workspaceUrlKey && (
                  <span className="ml-1 text-slate-500">
                    ({installation.workspaceUrlKey})
                  </span>
                )}
              </p>
            </div>

            {installation.teams && installation.teams.length > 0 ? (
              <div>
                <label className="block text-xs font-medium text-slate-300">
                  Team
                </label>
                <select
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                >
                  {!installation.defaultTeamId && (
                    <option value="">Pick a team…</option>
                  )}
                  {installation.teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.key})
                    </option>
                  ))}
                </select>
              </div>
            ) : installation.defaultTeamId ? (
              <p className="text-xs text-slate-400">
                Default team:{" "}
                <span className="text-slate-300">
                  {installation.defaultTeamName ?? "(unnamed)"}
                </span>
              </p>
            ) : (
              <p className="text-xs text-rose-300">
                No team picker — open the Account page and{" "}
                <a className="underline" href="/account#linear">
                  refresh teams
                </a>{" "}
                first.
              </p>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-300">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300">
                Description (Markdown)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={8}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-100"
              />
            </div>

            <div className="mt-1 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  busy ||
                  !title.trim() ||
                  // If teams are listed but none selected, block. Otherwise
                  // allow — server will surface a clear MISSING_TEAM_ID 400.
                  Boolean(
                    installation.teams &&
                      installation.teams.length > 0 &&
                      !teamId,
                  )
                }
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? "Filing…" : "Create issue"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function healCrawlId(heal: RunHealDetail): string {
  // RunHealDetail tracks two crawls (previous + new). Prefer the new one
  // because the heal proposals describe changes against it; fall back to
  // the previous if the heal pre-dates the new-crawl link.
  return heal.newCrawlId ?? heal.previousCrawlId ?? "(unknown crawl)";
}

function defaultIssueTitle(heal: RunHealDetail): string {
  // "Heal: 3 selectors changed in <crawlId>"
  const n = heal.totalCount;
  const noun = n === 1 ? "selector change" : "selector changes";
  return `Heal: ${n} ${noun} on ${healCrawlId(heal)}`;
}

function defaultIssueBody(heal: RunHealDetail): string {
  const lines = [
    `**UAIP heal proposal** — generated by self-healing.`,
    ``,
    `- Crawl: \`${healCrawlId(heal)}\``,
    heal.runId ? `- Run: \`${heal.runId}\`` : "",
    `- Source: ${heal.source}`,
    `- Status: ${heal.status}`,
    `- Decisions made: ${heal.decisions.length} of ${heal.totalCount}`,
    ``,
    `Open in UAIP: ${typeof window !== "undefined" ? window.location.origin : ""}/heals/${encodeURIComponent(heal.id)}`,
  ].filter(Boolean);
  return lines.join("\n");
}
